import AllowedDocument, {
  AllowedDocumentsSet,
} from "models/documents/allowed-document";
import {
  PaxCheckChecklistItemRequirement,
  PaxCheckDocumentRequirement,
  PaxCheckInfoItem,
  PaxCheckRuleOrigin,
} from "../../../api";
import ChecklistItemStatus, {
  ChecklistItemStatusFromApi,
} from "./checklist-item-status";

export type ChecklistItemId = string;

export interface ChecklistItemDocumentRequirement {
  id: string;
  itemType: string;
  isTravelDocument: boolean;
  title: string;
  shortTitle?: string | null;
  description?: string | null;
  shortDescription?: string | null;
  status: ChecklistItemStatus;
  requirements: Array<PaxCheckChecklistItemRequirement>;
  allowedDocumentsSet: AllowedDocumentsSet;
  externalLink?: string | null;
  ruleOrigin: PaxCheckRuleOrigin;
}

export interface ChecklistItemInfoItemProps {
  id: string;
  title: string;
  shortTitle?: string | null;
  description?: string | null;
  shortDescription?: string | null;
  status: ChecklistItemStatus;
  requirements: Array<PaxCheckChecklistItemRequirement>;
  externalLink?: string | null;
  ruleOrigin: PaxCheckRuleOrigin;
}

interface ChecklistItem {
  id: string;
  itemType: string;
  title: string;
  shortTitle?: string | null;
  description?: string | null;
  shortDescription?: string | null;
  status: ChecklistItemStatus;
  requirements: Array<PaxCheckChecklistItemRequirement>;
  externalLink?: string | null;
  ruleOrigin: PaxCheckRuleOrigin;
}

export class DocumentRequirement implements ChecklistItem {
  id: string;
  itemType: string;
  isTravelDocument: boolean;
  title: string;
  shortTitle?: string | null;
  description?: string | null;
  shortDescription?: string | null;
  status: ChecklistItemStatus;
  requirements: Array<PaxCheckChecklistItemRequirement>;
  allowedDocumentsSet: AllowedDocumentsSet;
  externalLink?: string | null;
  ruleOrigin: PaxCheckRuleOrigin;

  constructor(documentRequirement: ChecklistItemDocumentRequirement) {
    this.id = documentRequirement.id;
    this.itemType = documentRequirement.itemType;
    this.isTravelDocument = documentRequirement.isTravelDocument;
    this.title = documentRequirement.title;
    this.shortTitle = documentRequirement.shortTitle;
    this.description = documentRequirement.description;
    this.shortDescription = documentRequirement.shortDescription;
    this.status = documentRequirement.status;
    this.requirements = documentRequirement.requirements;
    this.allowedDocumentsSet = documentRequirement.allowedDocumentsSet;
    this.externalLink = documentRequirement.externalLink;
    this.ruleOrigin = documentRequirement.ruleOrigin;
  }

  static fromApi(c: PaxCheckDocumentRequirement): DocumentRequirement {
    const itemType = c.itemType.itemType;
    const itemTypeWithTerritory = c.itemType.itemTerritory
      ? `${itemType}-${c.itemType.itemTerritory}`
      : itemType;

    return new DocumentRequirement({
      id: c.id,
      itemType: itemTypeWithTerritory,
      isTravelDocument: c.isTravelDocument,
      title: c.title,
      shortTitle: c.shortTitle,
      description: c.description,
      shortDescription: c.shortDescription,
      status: ChecklistItemStatusFromApi(c.status),
      requirements: c.requirements || [],
      allowedDocumentsSet: AllowedDocumentsSet.fromApi(c.allowedDocumentsSet),
      externalLink: c.externalLink,
      ruleOrigin: c.ruleOrigin,
    });
  }

  getAllowedDocumentByType(type: string): AllowedDocument | undefined {
    const filtered = this.allowedDocumentsSet?.allowedDocuments.filter(
      (doc) => doc.type === type
    );
    return filtered && filtered.length > 0 ? filtered[0] : undefined;
  }
}

export class InfoItem implements ChecklistItem {
  id: string;
  itemType: string;
  title: string;
  shortTitle?: string | null;
  description?: string | null;
  shortDescription?: string | null;
  status: ChecklistItemStatus;
  requirements: Array<PaxCheckChecklistItemRequirement>;
  externalLink?: string | null;
  ruleOrigin: PaxCheckRuleOrigin;

  constructor(props: ChecklistItemInfoItemProps) {
    this.id = props.id;
    this.itemType = "INFO";
    this.title = props.title;
    this.shortTitle = props.shortTitle;
    this.description = props.description;
    this.shortDescription = props.shortDescription;
    this.status = props.status;
    this.requirements = props.requirements;
    this.externalLink = props.externalLink;
    this.ruleOrigin = props.ruleOrigin;
  }

  static fromApi(c: PaxCheckInfoItem): InfoItem {
    return new InfoItem({
      id: c.id,
      title: c.title,
      shortTitle: c.shortTitle,
      description: c.description,
      shortDescription: c.shortDescription,
      status: ChecklistItemStatusFromApi(c.status),
      requirements: c.requirements || [],
      externalLink: c.externalLink,
      ruleOrigin: c.ruleOrigin,
    });
  }
}

export default ChecklistItem;
